import React, { useState, useEffect, useRef, useCallback } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import { EmptyDataDiv, Footer, Header, ListingResultText, MixPanelTrackedLink, PageListCard, SearchInput } from "../../components";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { fundRaiserPageAction } from "../../store/actions";
import { FundraisersListSkeleton } from "../../components/skeletons";
import HelmetMetaData from "./HelmetMetaData";
import { FilterModal } from "../../components/modals";
import { fundraiserAPIs } from "../../api";
import { filtersState as initialFiltersState } from "../../states";
import InfiniteScroll from "react-infinite-scroll-component";
import { debounce } from "../../helpers";
import { FiltersIcon } from "../../components/Icons";
import { useNonInitialEffect } from "../../hooks/UseNonInitialEffect";
import { Box, Container, Grid, Stack } from "@mui/material";
import { isMobile } from "react-device-detect";
import Tracking from "../../services/tracking.service";
import { PAGENAME_CONSTANTS } from "../../constants";

const toId = (value) => {
    const numberValue = Number(value);
    return value !== '' && value !== null && value !== undefined && !Number.isNaN(numberValue) ? numberValue : value;
};

const toIdList = (value) => (value ? value.split(',').filter(Boolean).map(toId) : []);

const isPlaceholderGoal = (goalRange) =>
    Number(goalRange.min) === initialFiltersState.goalRange.min &&
    Number(goalRange.max) === initialFiltersState.goalRange.max;

// A goal range counts only when the visitor actually moved the slider.
// {min: 0, max: 1000} is the untouched default, and the country's full bounds are not a filter.
const isCustomGoalRange = (goalRange, range) => {
    if (!goalRange || isPlaceholderGoal(goalRange)) return false;
    if (range && Number(range.max_range) > 0) {
        return Number(goalRange.min) !== Number(range.min_range) || Number(goalRange.max) !== Number(range.max_range);
    }
    return true;
};

const countSelectedFilters = (filters, range) => {
    let count = 0;
    if (filters.countries.length) count++;
    if (filters.campaigns.length) count++;
    if (filters.occasions.length) count++;
    if (filters.pageType.length) count++;
    if (filters.countries.length === 1 && isCustomGoalRange(filters.goalRange, range)) count++;
    return count;
};

const filtersFromSearch = (search) => {
    const params = new URLSearchParams(search);
    const min = params.get('min');
    const max = params.get('max');
    const countries = toIdList(params.get('countries'));
    const hasGoal = countries.length === 1 && min !== null && max !== null && min !== '' && max !== '' && !Number.isNaN(Number(min)) && !Number.isNaN(Number(max));
    const filters = {
        ...initialFiltersState,
        offset: 0,
        searchString: params.get('q') || '',
        pageType: toIdList(params.get('pageType')),
        countries,
        campaigns: toIdList(params.get('campaigns')),
        occasions: toIdList(params.get('occasions')),
        goalRange: hasGoal ? { min: Number(min), max: Number(max) } : { ...initialFiltersState.goalRange },
    };
    filters.selectedFiltersCount = countSelectedFilters(filters, null);
    return filters;
};

const searchFromFilters = (filters, range) => {
    const params = new URLSearchParams();
    if (filters.searchString) params.set('q', filters.searchString);
    if (filters.pageType.length) params.set('pageType', filters.pageType.join(','));
    if (filters.countries.length) params.set('countries', filters.countries.join(','));
    if (filters.campaigns.length) params.set('campaigns', filters.campaigns.join(','));
    if (filters.occasions.length) params.set('occasions', filters.occasions.join(','));
    if (filters.countries.length === 1 && isCustomGoalRange(filters.goalRange, range)) {
        params.set('min', String(filters.goalRange.min));
        params.set('max', String(filters.goalRange.max));
    }
    return params.toString();
};

const queriesMatch = (left, right) => {
    const a = new URLSearchParams(left);
    const b = new URLSearchParams(right);
    const keys = new Set([...a.keys(), ...b.keys()]);
    for (const key of keys) {
        if (a.get(key) !== b.get(key)) return false;
    }
    return true;
};

const resolveGoalRange = (goalRange, range) => {
    if (!range || !Number(range.max_range)) return goalRange;
    const inBounds = Number(goalRange.min) >= Number(range.min_range)
        && Number(goalRange.max) <= Number(range.max_range)
        && Number(goalRange.min) <= Number(goalRange.max);
    if (!isPlaceholderGoal(goalRange) && inBounds) return goalRange;
    return { min: range.min_range, max: range.max_range };
};

export function FundRaiserList() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const restoredFilters = useRef(null);
    if (restoredFilters.current === null) {
        restoredFilters.current = filtersFromSearch(location.search);
    }
    const [state, setState] = useState(restoredFilters.current);
    const [searchText, setSearchText] = useState(restoredFilters.current.searchString);
    const startedFromUrl = useRef(restoredFilters.current.selectedFiltersCount > 0 || !!restoredFilters.current.searchString);
    const searchGeneration = useRef(0);
    const fetchId = useRef(0);
    const boundsGeneration = useRef(0);
    const initialFetchStarted = useRef(false);
    const [boundsStatus, setBoundsStatus] = useState({ countryId: null, settled: false });
    const [fundraisersList, setFundraisersList] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0)
    const [loadingPages, setLoadingPages] = useState(true);
    const [openFiltersModal, setOpenFiltersModal] = useState(false);
    const [loadingMorePages, setLoadingMorePages] = useState(false);
    const minMaxRange = useSelector(selectorState => selectorState.fundraiser_page.min_max_range);

    const locationDetails = useSelector(selectorState => selectorState.fundraiser_page.location_details);
    const TITLE = 'The Citizens Foundation';
    const DESCRIPTION = 'Fundraise for Education. Create your personal fundraiser and rally your friends and family to provide quality education to children in Pakistan.';

    const mapFundraiserPages = () => {
        return fundraisersList.map(list => {
            return <Grid key={list.id} item xs={6} sm={4} sx={styles.item}>
                <PageListCard page={list} key={list.id} />
            </Grid>
        })
    }

    const loadMorePages = () => {
        setLoadingMorePages(true);
        setState(prev => ({
            ...prev,
            offset: prev.offset + 21
        }))
    }
    const getAndSetFundraiserList = async (payload, applyingNewFilter = false) => {
        const id = ++fetchId.current;
        setLoadingPages(true);
        try {
            const response = await fundraiserAPIs.getAllFundraisers(payload)
            if (id !== fetchId.current) return;
            if (response && response.data && response.data.data) {
                setTotalRecords(response.data.data.total)
                const result = response.data.data.result || [];
                setFundraisersList(prev => applyingNewFilter ? [...result] : [...prev, ...result])
            }
        } finally {
            if (id === fetchId.current) {
                setLoadingPages(false);
                setLoadingMorePages(false);
            }
        }
    }

    const openModal = () => {
        setOpenFiltersModal(true)
    }

    const applyFilter = async (filters) => {
        const isReset = filters === initialFiltersState;
        const next = {
            ...initialFiltersState,
            ...filters,
            offset: 0,
            searchString: isReset ? '' : searchText,
            pageType: [...(filters.pageType || [])],
            countries: [...(filters.countries || [])],
            campaigns: [...(filters.campaigns || [])],
            occasions: [...(filters.occasions || [])],
            goalRange: { ...(filters.goalRange || initialFiltersState.goalRange) },
        };
        next.selectedFiltersCount = isReset ? 0 : countSelectedFilters(next, minMaxRange);
        searchGeneration.current += 1;
        setSearchText(next.searchString || '');
        setFundraisersList([]);
        setLoadingPages(true);
        setLoadingMorePages(false);
        const effectWillFetch = state.offset !== 0 || state.searchString !== next.searchString;
        setState(next);
        if (!effectWillFetch) await getAndSetFundraiserList(next, true)
        if (next.selectedFiltersCount !== 0) setOpenFiltersModal(false)
    }
    const resetFilters = (e) => {
        e.preventDefault();
        applyFilter(initialFiltersState);
    }

    const searchClick = () => {
        searchGeneration.current += 1;
        setLoadingMorePages(false);
        setState(prev => {
            if (prev.offset === 0 && prev.searchString === searchText) return prev;
            return {
                ...prev,
                offset: 0,
                searchString: searchText
            }
        })
    }
    useEffect(() => {
        dispatch(fundRaiserPageAction.getUserDefaultCurrency());
        Tracking.trackPage({pageName: PAGENAME_CONSTANTS.FUNDRAISERS})
    }, [])

    useEffect(() => {
        if (state.countries.length != 1) return undefined;
        const countryId = toId(state.countries[0]);
        const generation = ++boundsGeneration.current;
        let cancelled = false;
        dispatch(fundRaiserPageAction.getMinMaxGoalRange(countryId)).then(() => {
            if (cancelled || generation !== boundsGeneration.current) return;
            setBoundsStatus({ countryId, settled: true });
        });
        return () => { cancelled = true; };
    }, [state.countries])

    useEffect(() => {
        if (!boundsStatus.settled || state.countries.length != 1 || toId(state.countries[0]) !== boundsStatus.countryId) return;
        if (!minMaxRange || !Number(minMaxRange.max_range)) return;
        setState(prev => {
            if (prev.countries.length != 1 || toId(prev.countries[0]) !== boundsStatus.countryId) return prev;
            const goalRange = resolveGoalRange(prev.goalRange, minMaxRange);
            const selectedFiltersCount = countSelectedFilters({ ...prev, goalRange }, minMaxRange);
            if (Number(goalRange.min) === Number(prev.goalRange.min)
                && Number(goalRange.max) === Number(prev.goalRange.max)
                && selectedFiltersCount === prev.selectedFiltersCount) return prev;
            return {
                ...prev,
                goalRange,
                selectedFiltersCount
            };
        })
    }, [minMaxRange, boundsStatus, state.countries])

    useEffect(() => {
        if (initialFetchStarted.current) return;
        if (startedFromUrl.current) {
            const restored = restoredFilters.current;
            const needsBounds = restored.countries.length == 1 && isPlaceholderGoal(restored.goalRange);
            const boundsMatch = boundsStatus.settled && toId(restored.countries[0]) === boundsStatus.countryId;
            if (needsBounds && !boundsMatch) return;
            initialFetchStarted.current = true;
            const filters = {
                ...restored,
                offset: 0,
                goalRange: { ...restored.goalRange },
            };
            if (needsBounds && minMaxRange && Number(minMaxRange.max_range)) {
                filters.goalRange = { min: minMaxRange.min_range, max: minMaxRange.max_range };
            }
            getAndSetFundraiserList(filters, true);
            return;
        }
        if (!locationDetails) return;
        initialFetchStarted.current = true;
        const payload = {
            countries: [],
            offset: 0,
        }
        if (locationDetails.is_exist) {
            payload.countries = [locationDetails.country_id]
            setState(prev => ({
                ...prev,
                countries: [locationDetails.country_id],
                selectedFiltersCount: 1
            }))
        }
        getAndSetFundraiserList(payload, true)
    }, [locationDetails, boundsStatus, minMaxRange])

    useNonInitialEffect(() => {
        const query = searchFromFilters(state, minMaxRange);
        if (!queriesMatch(query, location.search)) {
            navigate({ pathname: location.pathname, search: query ? `?${query}` : '' }, { replace: true })
        }
    }, [state.countries, state.campaigns, state.occasions, state.pageType, state.searchString, state.goalRange, minMaxRange])

    useNonInitialEffect(() => {
        if (loadingMorePages)
            getAndSetFundraiserList(state);
        else if (state.offset == 0) {
            setFundraisersList([]);
            setLoadingPages(true);
            getAndSetFundraiserList(state, true);
        }
    }, [state.offset, state.searchString])

    const debouncedSearch = useCallback(debounce((searchString, generation) => {
        if (generation !== searchGeneration.current) return;
        setLoadingMorePages(false);
        setState(prev => {
            if (prev.offset === 0 && prev.searchString === searchString) return prev;
            return {
                ...prev,
                offset: 0,
                searchString
            };
        });
    }, 500), []);

    const handleSearchChange = e => {
        const value = e.target.value;
        searchGeneration.current += 1;
        setSearchText(value);
        debouncedSearch(value, searchGeneration.current);
    }

    return <Box className="App-display-height">
        <Header />
        <Container component="main" className="App-content">
            <FilterModal globalState={state} open={openFiltersModal} setOpen={setOpenFiltersModal} applyFilter={applyFilter} />
            <Stack direction="row" spacing={2}>
                <SearchInput
                    size="small"
                    placeholder="Search Here"
                    value={searchText}
                    sx={styles.searchField}
                    onChange={handleSearchChange}
                    onKeyDown={e => e.key == 'Enter' && searchClick()}
                />
                <MixPanelTrackedLink type="button"
                    label="Filter"
                    context="Fundraiser listing page"
                    variant="outlined"
                    onClick={openModal} startIcon={<FiltersIcon />}>
                    Filter
                </MixPanelTrackedLink>
            </Stack>
            <ListingResultText resultCount={totalRecords} filtersCount={state.selectedFiltersCount} resetHandler={resetFilters} />
            {fundraisersList.length ? (
                <InfiniteScroll
                    dataLength={fundraisersList.length} //This is important field to render the next data
                    next={loadMorePages}
                    hasMore={fundraisersList.length < totalRecords}
                    loader={<div style={{ textAlign: "center" }}><CircularProgress thickness={2} /></div>}
                    style={styles.infinityLoader}
                >
                    <Grid container justifyContent="space-between" spacing={{xs: 0, sm: 4}} sx={styles.container}>
                        {mapFundraiserPages()}
                        {/* Added the following item to balance the UI if a row contains two records */}
                        <Grid item xs={5.5} sm={3.5}></Grid>
                    </Grid>

                </InfiniteScroll>
            ) : loadingPages ? <FundraisersListSkeleton /> : <EmptyDataDiv message="No records found" />}
        </Container>
        <Footer />
    </Box>
}

const styles = {
    container: {
        gap: isMobile ? '5px' : 'unset',
        marginTop: '0 !important'
    },
    item: {
        maxWidth: isMobile ? 'calc(50% - 2.5px)' : 'unset'
    },
    searchField: {
        '& .MuiInputBase-input': {
            fontSize: '0.875rem'
        }
    },
    infinityLoader: {
        overflow: 'none',
    }
}
